import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Navigation, X, Loader, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useHotel } from '../context/HotelContext';
import apiClient from '../lib/apiClient';

const LocationSection = () => {
  const { hotel } = useHotel();
  const [showDirectionModal, setShowDirectionModal] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [manualLocation, setManualLocation] = useState('');
  const [locationError, setLocationError] = useState('');
  const [amenities, setAmenities] = useState([]);

  const hotelName = hotel?.hotelName || 'Hotel';
  const fullAddress = [hotel?.address, hotel?.city, hotel?.state, hotel?.country].filter(Boolean).join(', ') || '';
  const phone = hotel?.phone || hotel?.contactNumber || '';
  const email = hotel?.email || '';
  const lat = hotel?.latitude || null;
  const lng = hotel?.longitude || null;
  const locationDescription = hotel?.locationDescription || '';
  const nearbyAttractions = hotel?.nearByAttractions || [];
  const primaryColor = hotel?.primaryColor || '#2563EB';

  useEffect(() => {
    apiClient.get('/api/RoomFacility/GetAll')
      .then(data => setAmenities(Array.isArray(data) ? data : []))
      .catch(() => setAmenities([]));
  }, []);

  const getDirectionsFromCurrentLocation = () => {
    setIsGettingLocation(true);
    setLocationError('');

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          window.open(`https://www.google.com/maps/dir/${latitude},${longitude}/${lat},${lng}`, '_blank');
          setIsGettingLocation(false);
          setShowDirectionModal(false);
        },
        (error) => {
          setIsGettingLocation(false);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError('Location access denied. Please enable location access and try again.');
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError('Location information is unavailable. Please try manual entry.');
              break;
            case error.TIMEOUT:
              setLocationError('Location request timed out. Please try again.');
              break;
            default:
              setLocationError('An unknown error occurred. Please try manual entry.');
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 }
      );
    } else {
      setIsGettingLocation(false);
      setLocationError('Geolocation is not supported by this browser. Please use manual entry.');
    }
  };

  const getDirectionsFromManualLocation = () => {
    if (!manualLocation.trim()) {
      setLocationError('Please enter your location.');
      return;
    }
    window.open(`https://www.google.com/maps/dir/${encodeURIComponent(manualLocation)}/${lat},${lng}`, '_blank');
    setShowDirectionModal(false);
    setManualLocation('');
  };

  return (
    <section id="location" className="section-large bg-gray-50">
      <div className="container">
        <div className="text-center mb-12 animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s' }}>
          <h2 className="mb-4">Our Location</h2>
          {locationDescription && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{locationDescription}</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Map Section */}
          <div className="animate-slide-in-left opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="h-80 relative">
                {lat && lng ? (
                  <iframe
                    src={`https://maps.google.com/maps?q=${lat},${lng}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`${hotelName} Location`}
                    className="absolute inset-0"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-500">
                    <MapPin size={40} className="mb-2 opacity-30" />
                    <p className="text-sm">Location not set</p>
                  </div>
                )}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                  <div className="flex items-center text-sm font-medium text-gray-800">
                    <MapPin className="mr-2" style={{ color: primaryColor }} size={16} />
                    {hotelName}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
                <div className="space-y-3">
                  {fullAddress && (
                    <div className="flex items-start space-x-3">
                      <MapPin style={{ color: primaryColor }} className="mt-1 flex-shrink-0" size={18} />
                      <div>
                        <p className="font-medium">Address</p>
                        <p className="text-gray-600 text-sm">{fullAddress}</p>
                      </div>
                    </div>
                  )}
                  {phone && (
                    <div className="flex items-center space-x-3">
                      <Phone style={{ color: primaryColor }} className="flex-shrink-0" size={18} />
                      <div>
                        <p className="font-medium">Phone</p>
                        <p className="text-gray-600 text-sm">{phone}</p>
                      </div>
                    </div>
                  )}
                  {email && (
                    <div className="flex items-center space-x-3">
                      <Mail style={{ color: primaryColor }} className="flex-shrink-0" size={18} />
                      <div>
                        <p className="font-medium">Email</p>
                        <p className="text-gray-600 text-sm">{email}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Location Details */}
          <div className="space-y-8 animate-slide-in-right opacity-0" style={{ animationDelay: '0.3s' }}>
            {/* Amenities */}
            {amenities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-semibold mb-6">Hotel Amenities</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {amenities.map((amenity, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Star style={{ color: primaryColor }} className="mt-1 flex-shrink-0" size={20} />
                      <div>
                        <h4 className="font-medium mb-1">{amenity.name}</h4>
                        {amenity.description && (
                          <p className="text-sm text-gray-600">{amenity.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nearby Attractions */}
            {nearbyAttractions.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-semibold mb-6">Nearby Attractions</h3>
                <div className="space-y-4">
                  {nearbyAttractions.map((attraction, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium">{attraction.attractionName}</h4>
                        {attraction.category && (
                          <p className="text-sm text-gray-600">{attraction.category}</p>
                        )}
                      </div>
                      <div className="text-sm font-medium" style={{ color: primaryColor }}>
                        {attraction.attractionDistance}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Directions */}
            <div className="rounded-2xl shadow-lg p-8 text-white" style={{ background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}cc)` }}>
              <h3 className="text-xl font-semibold mb-4">Getting Here</h3>
              {locationDescription ? (
                <p className="text-white/90 mb-4">{locationDescription}</p>
              ) : (
                <p className="text-white/90 mb-4">{fullAddress}</p>
              )}
              <Button
                onClick={() => { setShowDirectionModal(true); setLocationError(''); }}
                className="mt-2 bg-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center space-x-2"
                style={{ color: primaryColor }}
              >
                <Navigation size={18} />
                <span>Get Directions</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Direction Modal */}
      {showDirectionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Get Directions to {hotelName}</h3>
                <button
                  onClick={() => { setShowDirectionModal(false); setLocationError(''); setManualLocation(''); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <X size={24} />
                </button>
              </div>

              {locationError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{locationError}</p>
                </div>
              )}

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-gray-900">Option 1: Use Your Current Location</h4>
                <Button
                  onClick={getDirectionsFromCurrentLocation}
                  disabled={isGettingLocation}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  {isGettingLocation ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span>Getting Your Location...</span>
                    </>
                  ) : (
                    <>
                      <Navigation size={18} />
                      <span>Auto-Detect My Location</span>
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  We'll detect your current location and open Google Maps with directions to our hotel.
                </p>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">OR</span>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-gray-900">Option 2: Enter Your Location Manually</h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={manualLocation}
                    onChange={(e) => setManualLocation(e.target.value)}
                    placeholder="Enter your address, area, or landmark"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors duration-200"
                    onKeyPress={(e) => { if (e.key === 'Enter') getDirectionsFromManualLocation(); }}
                  />
                  <Button
                    onClick={getDirectionsFromManualLocation}
                    variant="outline"
                    className="w-full flex items-center justify-center space-x-2"
                  >
                    <MapPin size={18} />
                    <span>Get Directions from This Location</span>
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <h5 className="font-medium text-blue-900 mb-2">Destination:</h5>
                <p className="text-sm text-blue-800">{fullAddress}</p>
                {phone && <p className="text-xs text-blue-600 mt-1">Phone: {phone}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LocationSection;
